/**
 * InputManager — toque e clique unificados.
 *
 * D2: so toque e clique. Nenhum listener de teclado existe neste arquivo, e
 * nenhum deve existir em lugar nenhum do jogo.
 * D3: alvo minimo de 80x80 px. Objetos clicaveis registram um raio de toque em
 *     pixels; se o objeto na tela for menor que o minimo, o raio compensa — a
 *     crianca acerta mesmo mirando torto.
 * D4: o retorno visual sai em menos de 200 ms, no mesmo quadro do toque.
 */

import * as THREE from 'three';

export const ALVO_MINIMO_PX = 80;

// Raio de encaixe do arrastar: bem folgado, porque mira de 5 anos e imprecisa.
const RAIO_ENCAIXE = 1.6;

export class InputManager {
  constructor(canvas, sceneManager) {
    this.canvas = canvas;
    this.sm = sceneManager;
    this.raycaster = new THREE.Raycaster();
    this.ponteiro = new THREE.Vector2();

    /** @type {Map<THREE.Object3D, Function>} objeto clicavel -> callback */
    this.clicaveis = new Map();
    /** @type {Map<THREE.Object3D, object>} objeto arrastavel -> configuracao */
    this.arrastaveis = new Map();
    this.arrastando = null;
    this.ativo = true;

    // pointerdown cobre dedo, caneta e mouse de uma vez so.
    canvas.addEventListener('pointerdown', (e) => this.aoTocar(e));
    canvas.addEventListener('pointermove', (e) => this.moverArrasto(e));
    canvas.addEventListener('pointerup', () => this.soltarArrasto());
    canvas.addEventListener('pointercancel', () => this.soltarArrasto());

    // O jogo roda em Chromebook: o menu de contexto por toque longo atrapalha.
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** Registra um objeto como tocavel. */
  registrar(objeto, aoClicar) {
    this.clicaveis.set(objeto, aoClicar);
  }

  desregistrar(objeto) {
    this.clicaveis.delete(objeto);
    this.arrastaveis.delete(objeto);
  }

  limpar() {
    this.clicaveis.clear();
    this.arrastaveis.clear();
  }

  // -------------------------------------------------------------------------
  // Arrastar e soltar — usado por distribuir, classificar, seriar, sequenciar
  // -------------------------------------------------------------------------

  /**
   * Torna um objeto arrastavel com o dedo.
   *
   * D6: soltar fora de qualquer destino NAO e erro. O objeto volta sozinho
   * para o lugar de origem, sem som de errado e sem perder nada.
   *
   * @param {THREE.Object3D} objeto
   * @param {object} opcoes
   * @param {Function} opcoes.aoSoltar  recebe (objeto, destino|null)
   * @param {Function} opcoes.destinos  devolve a lista de objetos-destino
   */
  registrarArrastavel(objeto, { aoSoltar, destinos }) {
    this.arrastaveis.set(objeto, { aoSoltar, destinos, origem: objeto.position.clone() });
  }

  iniciarArrasto(objeto, evento) {
    const config = this.arrastaveis.get(objeto);
    if (!config) return;

    this.arrastando = {
      objeto,
      config,
      origem: objeto.position.clone(),
      alturaVoo: objeto.position.y + 0.6,
    };

    // D4 — o objeto sobe na hora, para a crianca ver que "pegou".
    objeto.position.y = this.arrastando.alturaVoo;
    this.canvas.setPointerCapture?.(evento.pointerId);
  }

  moverArrasto(evento) {
    if (!this.arrastando) return;

    const ponto = this.pontoNoChao(evento);
    if (ponto) {
      this.arrastando.objeto.position.x = ponto.x;
      this.arrastando.objeto.position.z = ponto.z;
    }
  }

  soltarArrasto() {
    if (!this.arrastando) return;

    const { objeto, config, origem } = this.arrastando;
    this.arrastando = null;

    const destino = this.destinoMaisProximo(objeto, config.destinos());
    objeto.position.y = origem.y;

    if (!destino) {
      // D6 — voltar para a origem, sem drama.
      objeto.position.copy(origem);
    }

    config.aoSoltar(objeto, destino);
  }

  /** Onde o dedo esta tocando o chao da fazenda. */
  pontoNoChao(evento) {
    const retangulo = this.canvas.getBoundingClientRect();
    this.ponteiro.x = ((evento.clientX - retangulo.left) / retangulo.width) * 2 - 1;
    this.ponteiro.y = -((evento.clientY - retangulo.top) / retangulo.height) * 2 + 1;

    this.raycaster.setFromCamera(this.ponteiro, this.sm.camera);

    const plano = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const ponto = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(plano, ponto) ? ponto : null;
  }

  /** Destino mais proximo, dentro de um raio generoso (D3). */
  destinoMaisProximo(objeto, destinos) {
    let melhor = null;
    let menor = RAIO_ENCAIXE;

    for (const destino of destinos) {
      const distancia = objeto.position.distanceTo(destino.position);
      if (distancia < menor) {
        menor = distancia;
        melhor = destino;
      }
    }
    return melhor;
  }

  aoTocar(evento) {
    if (!this.ativo) return;

    // Arrastaveis tem prioridade: se o dedo pegou um, comeca o arrasto.
    if (this.arrastaveis.size > 0 && this.tentarPegarArrastavel(evento)) return;

    if (this.clicaveis.size === 0) return;

    const retangulo = this.canvas.getBoundingClientRect();
    const x = evento.clientX - retangulo.left;
    const y = evento.clientY - retangulo.top;

    this.ponteiro.x = (x / retangulo.width) * 2 - 1;
    this.ponteiro.y = -(y / retangulo.height) * 2 + 1;

    this.raycaster.setFromCamera(this.ponteiro, this.sm.camera);

    const objetos = [...this.clicaveis.keys()];
    const encontrados = this.raycaster.intersectObjects(objetos, true);

    if (encontrados.length > 0) {
      const atingido = this.encontrarRegistrado(encontrados[0].object);
      if (atingido) {
        this.clicaveis.get(atingido)(encontrados[0].point);
        return;
      }
    }

    // D3 — nada foi atingido diretamente. Procuramos o clicavel mais proximo
    // dentro do raio de tolerancia de 80 px. Um dedo de 5 anos erra por pouco,
    // e errar por pouco nao pode virar "nada aconteceu".
    const perto = this.maisProximoNaTela(x, y, retangulo);
    if (perto) this.clicaveis.get(perto)(null);
  }

  tentarPegarArrastavel(evento) {
    const retangulo = this.canvas.getBoundingClientRect();
    this.ponteiro.x = ((evento.clientX - retangulo.left) / retangulo.width) * 2 - 1;
    this.ponteiro.y = -((evento.clientY - retangulo.top) / retangulo.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ponteiro, this.sm.camera);

    const alvos = [...this.arrastaveis.keys()];
    const encontrados = this.raycaster.intersectObjects(alvos, true);
    if (encontrados.length === 0) return false;

    let atual = encontrados[0].object;
    while (atual && !this.arrastaveis.has(atual)) atual = atual.parent;
    if (!atual) return false;

    this.iniciarArrasto(atual, evento);
    return true;
  }

  /** Sobe na hierarquia ate achar o objeto que foi de fato registrado. */
  encontrarRegistrado(objeto) {
    let atual = objeto;
    while (atual) {
      if (this.clicaveis.has(atual)) return atual;
      atual = atual.parent;
    }
    return null;
  }

  maisProximoNaTela(x, y, retangulo) {
    let melhor = null;
    let menorDistancia = ALVO_MINIMO_PX / 2;

    for (const objeto of this.clicaveis.keys()) {
      const tela = this.paraTela(objeto, retangulo);
      const distancia = Math.hypot(tela.x - x, tela.y - y);
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        melhor = objeto;
      }
    }
    return melhor;
  }

  paraTela(objeto, retangulo) {
    const posicao = new THREE.Vector3();
    objeto.getWorldPosition(posicao);
    posicao.project(this.sm.camera);
    return {
      x: ((posicao.x + 1) / 2) * retangulo.width,
      y: ((-posicao.y + 1) / 2) * retangulo.height,
    };
  }

  /**
   * D3 — confere se um objeto ocupa pelo menos 80x80 px na tela.
   *
   * Mede a PEGADA do objeto na tela, nao a altura dele no mundo: um buraco no
   * chao e quase plano, e o que a crianca acerta com o dedo e a area vista de
   * cima. Medir a altura em Y daria falso alarme em tudo que fica deitado.
   *
   * A medida usa o enquadramento FINAL da camera, entao nao muda de resultado
   * conforme o zoom de foco anda.
   *
   * Nao corrige sozinho: avisa alto, porque tamanho de alvo e decisao de design.
   */
  conferirTamanhoDeAlvo(objeto, nome) {
    const caixa = new THREE.Box3().setFromObject(objeto);
    const tamanho = caixa.getSize(new THREE.Vector3());

    const pixelsPorUnidade = this.sm.pixelsPorUnidadeNoFoco();

    // Vista isometrica: o eixo vertical do mundo aparece encurtado na tela.
    const ENCURTAMENTO_ISO = 0.82;
    const larguraPx = Math.max(tamanho.x, tamanho.z) * pixelsPorUnidade;
    const alturaPx =
      Math.max(tamanho.z, tamanho.y, tamanho.x) * pixelsPorUnidade * ENCURTAMENTO_ISO;

    if (larguraPx < ALVO_MINIMO_PX || alturaPx < ALVO_MINIMO_PX) {
      console.warn(
        `FAZENDA: o alvo "${nome}" fica com ${Math.round(larguraPx)}x${Math.round(alturaPx)} px na tela, ` +
          `abaixo do minimo de ${ALVO_MINIMO_PX}x${ALVO_MINIMO_PX} px exigido pela regra D3. ` +
          'Aumente o objeto ou aproxime a camera no foco dessa ancora.'
      );
    }
    return { larguraPx, alturaPx };
  }
}
